import { Box, Tooltip } from "@mui/material";
import { useEffect, useState } from "react";
import { useAppSelector } from "redux/hooks";
import { selectCase, selectCaseClaimantName } from "redux/slices/case";
import { selectClientLens } from "redux/slices/clientLens";
import BreadcrumbDropdownPicker, { BreadcrumbDropdownPickerOption } from "./BreadcrumbDropdownPicker";
import { runGqlQuery } from "gqlClient";
import { type SearchCasesResult, SearchLensCasesDocument } from "generated/graphql";
import { useSnackbar } from "notistack";
import useDebounce from "helpers/useDebounce";
import { P } from "theme/typography";
import theme from "theme";
import { AppRoutes } from "AppRoutes";
import { fullName, fullNameFromParts } from "helpers/name-helpers";
import { stripNull } from "helpers/coll";

export const CaseHomeBreadcrumb = () => {
  const clientLens = useAppSelector(selectClientLens);
  const claimantName = useAppSelector(selectCaseClaimantName);
  const currentCase = useAppSelector(selectCase);
  const userAssignee = stripNull(currentCase.userAssignee);
  const [loading, setLoading] = useState<boolean>(false);
  const [textQuery, setTextQuery] = useState<string>("");
  const debouncedQuery = useDebounce(textQuery, 250);
  const { enqueueSnackbar } = useSnackbar();

  const [options, setOptions] = useState<BreadcrumbDropdownPickerOption[]>([]);
  const optionBuilder = (c: SearchCasesResult): BreadcrumbDropdownPickerOption => {
    return {
      title: c.claimantName,
      subtitleContent: (
        <Box sx={{ display: "flex", width: "100%", justifyContent: "space-between" }}>
          <Tooltip title="Case ID" arrow placement="right">
            <P sx={{ color: theme.palette.grey[300] }} variant="xsBold">
              {c.clientCaseId}
            </P>
          </Tooltip>
          <Tooltip title="Assignee" arrow placement="left">
            <P sx={{ color: theme.palette.grey[300], mr: 2 }} variant="xsBold">
              {c.assigneeFullName}
            </P>
          </Tooltip>
        </Box>
      ),
      uniqueId: c.caseId.toString(),
      route: AppRoutes.caseDetailBuilder(clientLens.id, c.caseId),
    };
  };

  const validQuery = debouncedQuery.length > 0;
  useEffect(() => {
    if (!validQuery) return;
    let canceled = false;
    setLoading(true);
    runGqlQuery(SearchLensCasesDocument, {
      lensId: clientLens.id,
      query: debouncedQuery,
      limit: 20,
    })
      .then((res) => {
        if (!canceled) {
          const options = res.lens.searchCases.map((c) => optionBuilder(c));
          setOptions(options);
          setLoading(false);
        }
      })
      .catch(() => {
        enqueueSnackbar("There was a problem loading the cases. Please try again later.", { variant: "error" });
        setLoading(false);
      });
    return () => {
      canceled = true;
    };
  }, [debouncedQuery]);

  const finalOptions = validQuery
    ? [...options]
    : [
        optionBuilder({
          caseId: currentCase.id,
          clientCaseId: currentCase.info.clientCaseId,
          claimantName: fullNameFromParts(currentCase.info.claimantGivenName, currentCase.info.claimantSurname),
          assigneeFullName: userAssignee !== undefined ? fullName(userAssignee) : "",
        }),
        ...options.filter((o) => o.uniqueId !== currentCase.id.toString()),
      ];

  return (
    <Box sx={{ display: "flex", alignItems: "center" }}>
      <BreadcrumbDropdownPicker
        pendoId="breadcrumb-casehome::caseId"
        search={true}
        // if searching, just show the results, otherwise move the current case to the top and remove duplicates
        options={finalOptions}
        searchPlaceholder="Search by case id or name"
        currentRoute={AppRoutes.caseDetailBuilder(clientLens.id, currentCase.id)}
        label={claimantName || "..."}
        noResultsText="No results found"
        loading={loading}
        key="casehome"
        onChange={(val) => {
          setTextQuery(val);
        }}
      />
    </Box>
  );
};
